import { useMemo, useState } from 'react';
import { SquareMinus, SquarePlus, X } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Skeleton } from 'shadcn/components/ui/Skeleton';
import { cn } from 'shadcn/lib/utils';

import { filterAisleTree } from './utils';

/**
 * Displays a nested list of sorted aisles.
 *
 * @param {number} activeAisleID - The ID of the active aisle
 * @param {Array} aisleTree - The aisle tree data
 * @param {boolean} loading - Whether the aisle tree is loading
 */
export default function AisleContentNav({ activeAisleID, aisleTree, loading }) {
    const [manuallyExpandedAisleIDs, setManuallyExpandedAisleIDs] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    const filteredAisleTree = useMemo(() => filterAisleTree(aisleTree, searchTerm), [aisleTree, searchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    return (
        <>
            <div className="shrink-0 w-94" />
            <div className="fixed top-34 bottom-0 flex flex-col w-94 overflow-hidden">
                <div className="relative py-4 px-2 border-b">
                    <Input placeholder="Search aisles..." value={searchTerm} onChange={handleSearchChange} />
                    {searchTerm && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={clearSearch}
                            className="absolute right-3 top-1/2 -translate-y-1/2 hover:bg-transparent"
                            aria-label="Clear search"
                        >
                            <X className="size-4 text-muted-foreground" />
                        </Button>
                    )}
                </div>

                <ol className="flex flex-col items-start gap-4 pt-4 pb-20 pl-4 overflow-y-auto flex-1">
                    {loading && <LoadingSkeleton />}
                    {!loading && filteredAisleTree.length === 0 && searchTerm && (
                        <li className="text-muted-foreground text-sm">
                            No aisles found matching &quot;{searchTerm}&quot;
                        </li>
                    )}
                    {!loading &&
                        filteredAisleTree.map((node) => (
                            <RecursiveAisleNode
                                key={node.id}
                                node={node}
                                activeAisleID={activeAisleID}
                                manuallyExpandedAisleIDs={manuallyExpandedAisleIDs}
                                searchTerm={searchTerm}
                                setManuallyExpandedAisleIDs={setManuallyExpandedAisleIDs}
                            />
                        ))}
                </ol>
            </div>
        </>
    );
}

/**
 * Checks if any of a node's descendants match the search term.
 * Does not include the node itself, only its descendants.
 *
 * @param {Object} node - The node to check
 * @param {string} searchTerm - The search term to match against
 * @returns {boolean} Whether any descendants match the search
 */
function hasMatchingDescendant(node, searchTerm) {
    if (!searchTerm.trim()) {
        return false;
    }

    const search = searchTerm.toLowerCase();

    // Check if any children or their descendants match
    return (
        node.children?.some((child) => {
            const nameMatches = child.name.toLowerCase().includes(search);
            const idMatches = child.id.toString().includes(search);

            // If this child matches, return true
            if (nameMatches || idMatches) {
                return true;
            }

            // Recursively check child's descendants
            return hasMatchingDescendant(child, searchTerm);
        }) ?? false
    );
}

/**
 * Recursively determines the expansion state of a node in the aisle tree.
 *
 * @param {number} activeAisleID - The ID of the active aisle
 * @param {Set} manuallyExpandedAisleIDs - The manually expanded aisle IDs
 * @param {Object} node - The node to check
 * @param {string} searchTerm - The current search term
 * @returns {Object}
 * @returns {boolean} isManuallyExpanded - Whether the node is manually expanded
 * @returns {boolean} isActive - Whether the node is active
 * @returns {boolean} hasActiveChild - Whether the node has an active child
 * @returns {boolean} hasSearchMatch - Whether the node should expand due to search results
 */
function getNodeExpansionState({ activeAisleID, manuallyExpandedAisleIDs, node, searchTerm }) {
    const isManuallyExpanded = manuallyExpandedAisleIDs?.has(node.id) ?? false;
    const isActive = node.id === activeAisleID;
    const hasActiveChild =
        node.children?.some((child) => {
            const { hasActiveChild: childHasActiveChild, isActive: childIsActive } = getNodeExpansionState({
                activeAisleID,
                manuallyExpandedAisleIDs,
                node: child,
                searchTerm,
            });
            return childHasActiveChild || childIsActive;
        }) ?? false;

    // Check if this node should expand due to search results
    const hasSearchMatch = searchTerm.trim() && hasMatchingDescendant(node, searchTerm);

    return { hasActiveChild, hasSearchMatch, isActive, isManuallyExpanded };
}

/**
 * Recursively renders a node in the aisle tree.
 *
 * @param {number} activeAisleID - The ID of the active aisle
 * @param {Set} manuallyExpandedAisleIDs - The manually expanded aisle IDs
 * @param {Object} node - The node to render
 * @param {Function} setManuallyExpandedAisleIDs - The function to set the manually expanded aisle IDs
 * @param {string} searchTerm - The current search term for highlighting
 */
function RecursiveAisleNode({
    activeAisleID,
    manuallyExpandedAisleIDs,
    node,
    searchTerm,
    setManuallyExpandedAisleIDs,
}) {
    const { hasActiveChild, hasSearchMatch, isActive, isManuallyExpanded } = getNodeExpansionState({
        activeAisleID,
        manuallyExpandedAisleIDs,
        node,
        searchTerm,
    });

    const handleAisleClick = () => {
        const el = document.getElementById(`aisle-content-section-${node.id}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleExpandClick = (e) => {
        e.stopPropagation();
        setManuallyExpandedAisleIDs((prev) => {
            const ids = new Set(prev);
            if (ids.has(node.id)) {
                ids.delete(node.id);
            } else {
                ids.add(node.id);
            }
            return ids;
        });
    };

    return (
        <li className="flex flex-col items-start gap-4">
            <div
                onClick={handleAisleClick}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleAisleClick()}
                role="button"
                tabIndex={0}
                className={cn(
                    `flex items-center gap-2 relative cursor-pointer scroll-m-4`,
                    hasActiveChild && 'font-black',
                    isActive && 'font-black before:content-["•"] before:absolute before:-left-4',
                )}
            >
                <HighlightedText searchTerm={searchTerm} text={node.name} />{' '}
                {node.children.length > 0 && isManuallyExpanded && (
                    <SquareMinus
                        onClick={handleExpandClick}
                        className="w-3.5 h-3.5 text-muted-foreground"
                        aria-label="Collapse aisle"
                    />
                )}
                {node.children.length > 0 && !isManuallyExpanded && (
                    <SquarePlus
                        onClick={handleExpandClick}
                        className="w-3.5 h-3.5 text-muted-foreground"
                        aria-label="Expand aisle"
                    />
                )}
            </div>
            {(isActive || isManuallyExpanded || hasActiveChild || hasSearchMatch) && node.children.length > 0 && (
                <ol className="flex flex-col items-start gap-4" style={{ marginLeft: `${node.level * 32}px` }}>
                    {node.children.map((child) => (
                        <RecursiveAisleNode
                            key={child.id}
                            node={child}
                            activeAisleID={activeAisleID}
                            manuallyExpandedAisleIDs={manuallyExpandedAisleIDs}
                            searchTerm={searchTerm}
                            setManuallyExpandedAisleIDs={setManuallyExpandedAisleIDs}
                        />
                    ))}
                </ol>
            )}
        </li>
    );
}

/**
 * Displays highlighted text in the node name based on search term.
 *
 * @param {string} text - The text to highlight
 * @param {string} searchTerm - The search term to highlight
 * @returns {JSX.Element} The text with highlighted matches
 */
function HighlightedText({ searchTerm, text }) {
    if (!searchTerm.trim()) {
        return text;
    }

    const search = searchTerm.toLowerCase();
    const lowerText = text.toLowerCase();
    const startIndex = lowerText.indexOf(search);

    if (startIndex === -1) {
        return text;
    }

    const beforeMatch = text.slice(0, startIndex);
    const match = text.slice(startIndex, startIndex + searchTerm.length);
    const afterMatch = text.slice(startIndex + searchTerm.length);

    return (
        <span>
            {beforeMatch}
            <span className="bg-yellow-200 font-medium">{match}</span>
            <HighlightedText searchTerm={searchTerm} text={afterMatch} />
        </span>
    );
}

/**
 * Renders a loading skeleton for the aisle content nav.
 */
function LoadingSkeleton() {
    const CLASS_NAMES = [
        'w-[20%] h-5',
        'w-[40%] h-5',
        'w-[30%] h-5',
        'ml-8 w-[50%] h-5',
        'ml-8 w-[40%] h-5',
        'ml-8 w-[60%] h-5',
        'w-[20%] h-5',
        'w-[30%] h-5',
        'w-[30%] h-5',
        'w-[20%] h-5',
        'w-[40%] h-5',
        'w-[30%] h-5',
        'w-[50%] h-5',
        'w-[20%] h-5',
        'w-[30%] h-5',
    ];

    return CLASS_NAMES.map((className, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index} className="flex w-full">
            <Skeleton className={className} />
        </li>
    ));
}
